import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Clapperboard, Loader2, Download, ChevronDown, ChevronUp, Coins } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../../context/LanguageContext';
import { myiaTexts } from '../../utils/myiaTranslations';
import { ReportAIContentButton } from '../ReportAIContent';

const LANGS = [
  ['fr', 'Français'], ['en', 'English'], ['es', 'Español'], ['de', 'Deutsch'],
  ['it', 'Italiano'], ['pt', 'Português'], ['nl', 'Nederlands'], ['ar', 'العربية'],
  ['ru', 'Русский'], ['zh', '中文'], ['ja', '日本語'], ['hi', 'हिन्दी'],
];

export default function DubbingPanel() {
  const { language } = useLanguage();
  const M = myiaTexts(language);
  const [open, setOpen] = useState(false);
  const [quota, setQuota] = useState(null);
  const [file, setFile] = useState(null);
  const [duration, setDuration] = useState(0);
  const [lang, setLang] = useState('en');
  const [busy, setBusy] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [downloading, setDownloading] = useState(null);
  const inputRef = useRef(null);
  const pollRef = useRef(null);

  const loadQuota = () => axios.get('/api/dubbing/quota').then(r => setQuota(r.data)).catch(() => {});
  const loadJobs = () => axios.get('/api/dubbing/mine').then(r => setJobs(r.data.jobs || [])).catch(() => {});

  useEffect(() => {
    if (!open) return;
    loadQuota(); loadJobs();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const hasPending = jobs.some(j => j.status === 'processing');
    if (!hasPending) return;
    pollRef.current = setInterval(async () => {
      for (const j of jobs.filter(x => x.status === 'processing')) {
        try { await axios.get(`/api/dubbing/${j.id}/status`); } catch {}
      }
      loadJobs();
    }, 15000);
    return () => clearInterval(pollRef.current);
  }, [open, jobs]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 200 * 1024 * 1024) { toast.error(M.dbTooBig); return; }
    const url = URL.createObjectURL(f);
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      if (v.duration > 20 * 60) { toast.error(M.dbTooLong); setFile(null); return; }
      setDuration(v.duration || 0);
      setFile(f);
    };
    v.onerror = () => { URL.revokeObjectURL(url); setFile(f); setDuration(0); };
    v.src = url;
  };

  const minutes = Math.max(1, Math.ceil((duration || 60) / 60));
  const cost = minutes * 10;
  const freeAvailable = quota?.sub_active && quota?.free_left > 0;

  const run = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('target_lang', lang);
      fd.append('duration_sec', String(duration || 60));
      await axios.post('/api/dubbing/create', fd, { timeout: 600000 });
      toast.success(M.dbOk);
      setFile(null); setDuration(0);
      if (inputRef.current) inputRef.current.value = '';
      loadQuota(); loadJobs();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(e?.response?.status === 402 ? M.dbNeedCredits : (d || M.dbKo));
    } finally { setBusy(false); }
  };

  const download = async (job) => {
    setDownloading(job.id);
    try {
      const r = await axios.get(`/api/dubbing/${job.id}/download`, { responseType: 'blob', timeout: 600000 });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(job.filename || 'video').replace(/\.[^.]+$/, '')}-dub-${job.target_lang}.mp4`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error(M.dbKo); }
    finally { setDownloading(null); }
  };

  return (
    <div className="rounded-2xl bg-gradient-to-r from-cyan-500/15 to-blue-500/15 border border-cyan-500/40 overflow-hidden" data-testid="dubbing-panel">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full p-5 text-left group flex items-center gap-4 hover:bg-cyan-500/10 transition-colors"
        data-testid="dubbing-toggle"
      >
        <div className="p-3 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl group-hover:scale-110 transition-transform">
          <Clapperboard className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h4 className="font-bold text-cyan-200">{M.dbTitle}</h4>
          <p className="text-xs text-gray-400 mt-1 line-clamp-2">{M.dbSub}</p>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-cyan-300" /> : <ChevronDown className="w-4 h-4 text-cyan-300" />}
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-3">
          {quota && (
            <div className="flex flex-wrap gap-2 text-[11px]">
              {quota.sub_active && (
                <span className="px-2.5 py-1 rounded-full bg-cyan-500/20 text-cyan-200" data-testid="dubbing-free-quota">
                  {M.dbQuota.replace('{n}', String(quota.free_left))}
                </span>
              )}
              <span className="px-2.5 py-1 rounded-full bg-yellow-500/20 text-yellow-200 inline-flex items-center gap-1" data-testid="dubbing-credits">
                <Coins className="w-3 h-3" /> {M.dbCreditsLabel}: {quota.credits}
              </span>
            </div>
          )}

          <input ref={inputRef} type="file" accept="video/*" onChange={onFile} className="hidden" data-testid="dubbing-file-input" />
          <button
            onClick={() => inputRef.current?.click()}
            className="w-full py-2.5 rounded-xl bg-white/10 hover:bg-white/15 border border-white/10 text-sm text-white"
            data-testid="dubbing-pick-btn"
          >
            {file ? `${file.name} · ${Math.round(duration)}s` : M.dbPick}
          </button>

          <div>
            <label className="block text-[11px] text-gray-400 mb-1">{M.dbLangLabel}</label>
            <select
              value={lang}
              onChange={e => setLang(e.target.value)}
              className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-sm text-white"
              data-testid="dubbing-lang-select"
            >
              {LANGS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
          </div>

          {file && !freeAvailable && (
            <p className="text-[11px] text-yellow-200/80">{M.dbCost.replace('{c}', String(cost)).replace('{m}', String(minutes))}</p>
          )}

          <button
            onClick={run}
            disabled={!file || busy}
            className="w-full py-3 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:brightness-110 text-white font-semibold text-sm disabled:opacity-40 inline-flex items-center justify-center gap-2"
            data-testid="dubbing-run-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clapperboard className="w-4 h-4" />}
            {busy ? M.dbRunning : M.dbRun}
          </button>

          {jobs.length > 0 ? (
            <div className="space-y-2 pt-1">
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">{M.dbJobs}</p>
              {jobs.map(j => (
                <div key={j.id} className="flex items-center gap-2 p-2.5 rounded-xl bg-black/30 border border-white/10" data-testid={`dubbing-job-${j.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-white truncate">{j.filename} → {j.target_lang.toUpperCase()}</p>
                    <p className="text-[10px] text-gray-500">
                      {j.status === 'done' ? M.dbDone : j.status === 'failed' ? M.dbFailed : M.dbProcessing}
                      {j.used_free ? ' · Free' : j.credits_charged ? ` · ${j.credits_charged} cr.` : ''}
                    </p>
                  </div>
                  {j.status === 'processing' && <Loader2 className="w-4 h-4 animate-spin text-cyan-300 shrink-0" />}
                  {j.status === 'done' && (
                    <>
                    <ReportAIContentButton feature="dubbing" iconOnly className="p-1.5 rounded-full bg-white/10 hover:bg-orange-500/30 text-orange-200 shrink-0" testId={`ai-report-dubbing-${j.id}`} />
                    <button
                      onClick={() => download(j)}
                      disabled={downloading === j.id}
                      className="px-3 py-1.5 rounded-full bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-200 text-[11px] inline-flex items-center gap-1 shrink-0 disabled:opacity-50"
                      data-testid={`dubbing-download-${j.id}`}
                    >
                      {downloading === j.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                      {M.dbDownload}
                    </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-gray-500 text-center pt-1">{M.dbNoJobs}</p>
          )}
        </div>
      )}
    </div>
  );
}
